/*
Copyright (c) 2025 Mathilde Meyer, Naziha Beghdadi
Licensed under the MIT License (see LICENSE.txt file for details)
*/

import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Container,
  Chip,
  Divider,
} from "@mui/material";
import ScienceIcon from '@mui/icons-material/Science';
import TimelineIcon from '@mui/icons-material/Timeline';
import DescriptionIcon from '@mui/icons-material/Description';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PeopleIcon from '@mui/icons-material/People';

export default function IntroductionStartCard({ pageCount, setPageCount, handleStart, projectName, setProjectName, handleJsonImport }) {
  return (
    <Box sx={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
      py: 6,
    }}>
      <Container maxWidth="lg">
        {/* Hero Section */}
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Box sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 2,
            mb: 3,
          }}>
            <ScienceIcon sx={{ fontSize: 60, color: "primary.main" }} />
          </Box>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              mb: 2,
              background: "linear-gradient(135deg, #1E3A8A 0%, #3B82F6 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Assisted Design of Pre-Clinical Research
          </Typography>
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{ mb: 4, fontWeight: 400, maxWidth: 700, mx: "auto" }}
          >
            A comprehensive tool for planning animal research protocols and preparing applications for authorization in Switzerland
          </Typography>
        </Box>

        {/* Centered Form */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 6 }}>
          <Card sx={{
            maxWidth: 600,
            width: "100%",
            boxShadow: "0 12px 48px rgba(30, 58, 138, 0.15)",
            borderRadius: 3,
            background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
            border: "2px solid",
            borderColor: "primary.light",
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography
                variant="h5"
                gutterBottom
                textAlign="center"
                fontWeight={700}
                color="primary.main"
                sx={{ mb: 1 }}
              >
                Get Started
              </Typography>
              <Typography
                variant="body2"
                textAlign="center"
                color="text.secondary"
                sx={{ mb: 4 }}
              >
                Create a new project (or import previous project)
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1.5, fontWeight: 600, color: "text.primary" }}
                >
                  Key Definitions
                </Typography>
                <Box sx={{
                  bgcolor: "grey.50",
                  p: 2.5,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "grey.200"
                }}>
                  <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.7 }}>
                    <strong>Experiment:</strong> A series of interventions on one or more animal groups
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.7 }}>
                    <strong>Intervention:</strong> An action performed on an animal
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.7 }}>
                    <strong>Procedure:</strong> Detailed intervention description
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                    <strong>Acclimatation:</strong> Mandatory pre-intervention rest period
                  </Typography>
                </Box>
              </Box>

              <TextField
                label="Project Name"
                variant="outlined"
                fullWidth
                required
                placeholder="e.g., Neuroscience Study 2025"
                sx={{ mb: 2.5 }}
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
              />

              <TextField
                label="Number of Experiments"
                variant="outlined"
                fullWidth
                required
                type="number"
                placeholder="Enter a number"
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                      borderWidth: '1px',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: 'text.secondary',
                  },
                }}
                value={pageCount}
                onChange={(e) => setPageCount(e.target.value)}
                slotProps={{ htmlInput: { min: 1 } }}
                helperText="Each distinct experimental protocol counts as one experiment. For example, two groups of mice treated differently = 1 experiment with 2 groups. Two entirely different protocols = 2 experiments."
              />

              <Button
                variant="contained"
                color="primary"
                fullWidth
                size="medium"
                onClick={handleStart}
                disabled={!pageCount || !projectName}
                sx={{
                  fontWeight: 700,
                  boxShadow: "0 4px 14px rgba(30, 58, 138, 0.25)",
                }}
              >
                Start New Project
              </Button>

              <Divider sx={{ my: 2.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
                  or
                </Typography>
              </Divider>

              <Button
                component="label"
                variant="outlined"
                color="primary"
                fullWidth
                size="medium"
                startIcon={<UploadFileIcon />}
                sx={{ fontWeight: 600 }}
              >
                Import existing project (JSON)
                <input
                  type="file"
                  accept=".json"
                  hidden
                  onChange={handleJsonImport}
                />
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", mt: 0.75 }}>
                Load a previously saved project — all fields will be restored automatically.
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Information Cards */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {/* Purpose Card */}
          <Card sx={{
            boxShadow: "0 8px 32px rgba(30, 58, 138, 0.12)",
            borderRadius: 3,
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              transform: "translateY(-4px)",
              boxShadow: "0 12px 48px rgba(30, 58, 138, 0.18)",
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <DescriptionIcon sx={{ fontSize: 32, color: "primary.main", mr: 1.5 }} />
                <Typography variant="h6" fontWeight={700}>
                  About This Tool
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                This application assists researchers in creating comprehensive Form A documents
                for animal experimentation protocols. It streamlines the process of documenting
                experimental procedures, timelines, and ethical considerations required for
                regulatory approval.
              </Typography>
            </CardContent>
          </Card>

          {/* Features + Developers row */}
          <Box sx={{ display: "flex", gap: 4, flexDirection: { xs: "column", md: "row" } }}>
            {/* Features Card */}
            <Card sx={{
              flex: 1,
              boxShadow: "0 8px 32px rgba(30, 58, 138, 0.12)",
              borderRadius: 3,
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0 12px 48px rgba(30, 58, 138, 0.18)",
              }
            }}>
              <CardContent sx={{ p: 3, display: "flex", flexDirection: "column", height: "100%" }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <TimelineIcon sx={{ fontSize: 32, color: "secondary.main", mr: 1.5 }} />
                  <Typography variant="h6" fontWeight={700}>
                    Key Features
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, flex: 1 }}>
                  <Typography variant="body2" sx={{ display: "flex", alignItems: "center" }}>
                    <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "primary.main", mr: 1.5 }} />
                    Interactive timeline visualization
                  </Typography>
                  <Typography variant="body2" sx={{ display: "flex", alignItems: "center" }}>
                    <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "primary.main", mr: 1.5 }} />
                    Automated FormA template generation
                  </Typography>
                  <Typography variant="body2" sx={{ display: "flex", alignItems: "center" }}>
                    <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "primary.main", mr: 1.5 }} />
                    Export/import functionality
                  </Typography>
                  <Typography variant="body2" sx={{ display: "flex", alignItems: "center" }}>
                    <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "primary.main", mr: 1.5 }} />
                    Sections 20-40 compliance
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Developers Card */}
            <Card sx={{
              flex: 1,
              boxShadow: "0 8px 32px rgba(30, 58, 138, 0.12)",
              borderRadius: 3,
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0 12px 48px rgba(30, 58, 138, 0.18)",
              }
            }}>
              <CardContent sx={{ p: 3, display: "flex", flexDirection: "column", height: "100%" }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <PeopleIcon sx={{ fontSize: 32, color: "success.main", mr: 1.5 }} />
                  <Typography variant="h6" fontWeight={700}>
                    Development Team
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.8 }}>
                  Developed at the University of Geneva for the scientific research community.
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2, flex: 1 }}>
                  <Chip
                    label="Mathilde Meyer"
                    color="primary"
                    variant="outlined"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                  <Chip
                    label="Naziha Beghdadi"
                    color="primary"
                    variant="outlined"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                  <Chip
                    label="Fatma Alobeidli"
                    color="primary"
                    variant="outlined"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                  <Chip
                    label="Rheeya Durbarry"
                    color="primary"
                    variant="outlined"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                  <Chip
                    label="Oriance Oternaud"
                    color="primary"
                    variant="outlined"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="caption" color="text.secondary">
                  MIT License © 2025
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
